import type { IUserInfo } from "@/account/account-models";
import { FullscreenOverlay } from "@/common/fullscreen-overlay";
import { PopupWrapper } from "@/common/popups/common/popup-wrapper";
import { PopupMessagingDisabled } from "@/common/popups/popup-messenger/popup-messaging-disabled";
import { PopupWarningGlobalFM } from "@/common/popups/popup-messenger/popup-warning-global-fm";
import { PopupWarningPaymentUser } from "@/common/popups/popup-messenger/popup-warning-payment-user";
import { publishTypingState } from "@/pubnub/pubnub-helpers";
import type { ITypingPublishConfig } from "@/pubnub/pubnub-models";
import { findArrayIndex, setInArray } from "@/utils/array";
import type { IPopupContextValue } from "@/utils/popup-provider";
import { defined } from "@/utils/variable-evaluation";
import debounce from "lodash.debounce";
import type { IMessageInfo } from "./messenger-models";

/* Reducer messages */

export function nextMessagesWithErrorMessage<K extends keyof IMessageInfo>(
  messages: IMessageInfo[],
  payload: IMessageInfo,
  idKey: K
) {
  const errorMessageIndex = findArrayIndex(messages, (message) => {
    return defined(message[idKey]) && message[idKey] === payload[idKey];
  });
  if (errorMessageIndex > -1) {
    const errorMessage = {
      ...messages[errorMessageIndex],
      error: payload.error,
    };
    return setInArray(messages, errorMessageIndex, errorMessage);
  } else if (defined(messages)) {
    return [...messages, { ...payload, error: payload.error }];
  } else {
    return [];
  }
}

export function formatCurrentMessage(message: string) {
  const formattedMessage = message.trim();
  if (formattedMessage[formattedMessage.length - 1] === "↵") {
    formattedMessage.slice(0, -1);
  }
  return formattedMessage;
}

export function popupMessagingDisabled(
  openPopup: IPopupContextValue["openPopup"]
) {
  openPopup((close) => (
    <div>
      <FullscreenOverlay isLanding isTransparent>
        <PopupWrapper onClose={close}>
          <PopupMessagingDisabled onClose={close} />
        </PopupWrapper>
      </FullscreenOverlay>
    </div>
  ));
}

export function popupWarningPaymentUser(
  openPopup: IPopupContextValue["openPopup"]
) {
  openPopup((close) => (
    <div>
      <FullscreenOverlay isLanding isTransparent>
        <PopupWrapper onClose={close}>
          <PopupWarningPaymentUser onUnderstand={close} />
        </PopupWrapper>
      </FullscreenOverlay>
    </div>
  ));
}

export function popupWarningGlobalFM(
  openPopup: IPopupContextValue["openPopup"]
) {
  openPopup((close) => (
    <div>
      <FullscreenOverlay isLanding isTransparent>
        <PopupWrapper onClose={close}>
          <PopupWarningGlobalFM onUnderstand={close} />
        </PopupWrapper>
      </FullscreenOverlay>
    </div>
  ));
}

const TYPING_STATE_TIMEOUT = 2000; // 2 seconds

export const debouncedTypingStart = debounce(
  (user: IUserInfo, typingSetStateConfig: ITypingPublishConfig) => {
    publishTypingState(user, typingSetStateConfig, true);
  },
  TYPING_STATE_TIMEOUT,
  {
    leading: true,
    trailing: false,
  }
);

export const debouncedTypingStop = debounce(
  (user: IUserInfo, typingSetStateConfig: ITypingPublishConfig) => {
    publishTypingState(user, typingSetStateConfig, false);
  },
  TYPING_STATE_TIMEOUT,
  {
    leading: false,
    trailing: true,
  }
);
